import { dump, load, Schema, DEFAULT_SCHEMA, type Type } from 'js-yaml';
import {
  hasErrors,
  issue,
  isRecord,
  joinSkillMd,
  normalizeWorkflow,
  orderFrontmatter,
  orderWorkflow,
  splitSkillMd,
  type Issue,
  type Workflow,
} from './core/index.ts';

export interface ParsedSkill {
  kind: 'instruction' | 'workflow';
  frontmatter: Record<string, unknown>;
  prose: string;
  workflow: Workflow | null;
  issues: Issue[];
}

const DUMP_OPTIONS = { lineWidth: -1, noRefs: true, quotingType: '"' as const };

// YAML anchors let aliases share and multiply objects. An alias needs an anchor, so refusing anchors refuses both.
// The parser reports anchors itself, so text like "*hot* surface" is never mistaken for an alias.
// js-yaml keeps the node's anchor on its state; the published types leave it out.
function refuseAnchors(event: string, state: unknown): void {
  if (event === 'close' && (state as { anchor?: string | null }).anchor) {
    throw new Error('anchors and aliases are not allowed');
  }
}

// js-yaml's default schema without its date resolver: a date such as 2026-10-05 stays text, which every
// field here expects, and merge keys with an inline mapping keep working. The published types leave
// the schema's type lists out.
const DEFAULT_TYPES = DEFAULT_SCHEMA as unknown as { implicit: (Type & { tag: string })[]; explicit: Type[] };
const NO_DATES = new Schema({
  implicit: DEFAULT_TYPES.implicit.filter((type) => type.tag !== 'tag:yaml.org,2002:timestamp'),
  explicit: DEFAULT_TYPES.explicit,
});

function loadPlain(source: string): unknown {
  const value = load(source, { listener: refuseAnchors, schema: NO_DATES });
  const seen = new Set<object>();
  const walk = (node: unknown) => {
    if (node === null || typeof node !== 'object') return;
    if (seen.has(node)) throw new Error('anchors and aliases are not allowed');
    seen.add(node);
    for (const child of Object.values(node)) walk(child);
  };
  walk(value);
  return value;
}

function reason(e: unknown): string {
  return e instanceof Error ? e.message.split('\n')[0] : 'unknown error';
}

export function parseSkillMd(md: string): ParsedSkill {
  const parts = splitSkillMd(md);
  const issues: Issue[] = [];
  let frontmatter: Record<string, unknown> = {};
  if (parts.frontmatterSource === null) {
    issues.push(issue('error', 'bad_frontmatter', 'The file must start with a frontmatter block between --- lines.'));
  } else {
    try {
      const loaded = loadPlain(parts.frontmatterSource);
      if (isRecord(loaded)) frontmatter = loaded;
      else issues.push(issue('error', 'bad_frontmatter', 'The frontmatter must be a list of key: value lines.'));
    } catch (e) {
      issues.push(issue('error', 'bad_frontmatter', `The frontmatter is not valid YAML: ${reason(e)}`));
    }
  }
  if (parts.workflowBlockCount > 1) {
    issues.push(issue('error', 'multiple_workflow_blocks', 'A skill can have only one workflow block.'));
  }
  if (parts.unterminated) {
    issues.push(issue('error', 'bad_structure', 'The workflow block is not closed with a ``` line.'));
  }
  let workflow: Workflow | null = null;
  if (parts.workflowSource !== null) {
    try {
      const normalized = normalizeWorkflow(loadPlain(parts.workflowSource) ?? {});
      workflow = normalized.workflow;
      issues.push(...normalized.issues);
    } catch (e) {
      issues.push(issue('error', 'bad_yaml', `The workflow block is not valid YAML: ${reason(e)}`));
    }
  }
  return { kind: workflow ? 'workflow' : 'instruction', frontmatter, prose: parts.prose, workflow, issues };
}

export function serializeSkillMd(skill: Pick<ParsedSkill, 'frontmatter' | 'prose' | 'workflow'>): string {
  const frontmatter = dump(orderFrontmatter(skill.frontmatter), DUMP_OPTIONS);
  const workflow = skill.workflow ? dump(orderWorkflow(skill.workflow), DUMP_OPTIONS) : null;
  return joinSkillMd(frontmatter, skill.prose, workflow);
}

/** True when two files say the same thing, however they are spelled. An unreadable file only equals its own text. */
export function sameSkillMd(a: string, b: string): boolean {
  if (a === b) return true;
  const left = parseSkillMd(a);
  const right = parseSkillMd(b);
  if (hasErrors(left.issues) || hasErrors(right.issues)) return false;
  return serializeSkillMd(withoutEmptyWorkflow(left)) === serializeSkillMd(withoutEmptyWorkflow(right));
}

// The editor drops a workflow block with no steps when it saves, so such a block says nothing.
function withoutEmptyWorkflow(skill: ParsedSkill): ParsedSkill {
  const steps = skill.workflow?.sections.some((section) => section.steps.length > 0) ?? false;
  return steps ? skill : { ...skill, workflow: null };
}
